import React, { useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Stack,
  InputAdornment,
  Divider,
  Alert
} from '@mui/material';

const CutAndSewCostForm = ({ costs, onChange }) => {
  const data = costs?.cutAndSew || {};
  const materials = data.materials || {};
  const labor = data.labor || {};
  const trims = data.trims || {};

  // Auto-calculate total manufacturing cost
  useEffect(() => {
    const fabricCost = parseFloat(materials.fabric) || 0;
    const notionsCost = parseFloat(materials.notions) || 0;
    const cuttingCost = parseFloat(labor.cutting) || 0;
    const sewingCost = parseFloat(labor.sewing) || 0;
    const finishingCost = parseFloat(labor.finishing) || 0;
    const labelsCost = parseFloat(trims.labels) || 0;
    const hardwareCost = parseFloat(trims.hardware) || 0;
    const otherTrimsCost = parseFloat(trims.other) || 0;
    const packagingCost = parseFloat(data.packaging) || 0;

    const total = fabricCost + notionsCost + cuttingCost + sewingCost +
                  finishingCost + labelsCost + hardwareCost +
                  otherTrimsCost + packagingCost;

    if (total !== data.totalManufacturingCost) {
      onChange({
        ...costs,
        cutAndSew: {
          ...data,
          totalManufacturingCost: total > 0 ? parseFloat(total.toFixed(2)) : undefined
        }
      });
    }
  }, [materials, labor, trims, data.packaging]);

  const handleChange = (category, field) => (event) => {
    const value = event.target.value;
    const numValue = value === '' ? undefined : parseFloat(value);

    if (category) {
      onChange({
        ...costs,
        cutAndSew: {
          ...data,
          [category]: {
            ...(data[category] || {}),
            [field]: numValue
          }
        }
      });
    } else {
      onChange({
        ...costs,
        cutAndSew: {
          ...data,
          [field]: numValue
        }
      });
    }
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Cut & Sew Production Costs
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Building garments from raw materials - enter per-unit costs
        </Typography>

        <Stack spacing={3}>
          {/* Materials */}
          <Box>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
              Materials
            </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Fabric Cost"
                type="number"
                size="small"
                value={materials.fabric ?? ''}
                onChange={handleChange('materials', 'fabric')}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
                inputProps={{ min: 0, step: '0.01' }}
                fullWidth
              />
              <TextField
                label="Thread & Notions"
                type="number"
                size="small"
                value={materials.notions ?? ''}
                onChange={handleChange('materials', 'notions')}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
                inputProps={{ min: 0, step: '0.01' }}
                fullWidth
              />
            </Stack>
          </Box>

          <Divider />

          {/* Labor */}
          <Box>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
              Labor
            </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Cutting"
                type="number"
                size="small"
                value={labor.cutting ?? ''}
                onChange={handleChange('labor', 'cutting')}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
                inputProps={{ min: 0, step: '0.01' }}
                fullWidth
              />
              <TextField
                label="Sewing"
                type="number"
                size="small"
                value={labor.sewing ?? ''}
                onChange={handleChange('labor', 'sewing')}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
                inputProps={{ min: 0, step: '0.01' }}
                fullWidth
              />
              <TextField
                label="Finishing/QC"
                type="number"
                size="small"
                value={labor.finishing ?? ''}
                onChange={handleChange('labor', 'finishing')}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
                inputProps={{ min: 0, step: '0.01' }}
                fullWidth
              />
            </Stack>
          </Box>

          <Divider />

          {/* Trims & Components */}
          <Box>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
              Trims & Components
            </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Labels/Tags"
                type="number"
                size="small"
                value={trims.labels ?? ''}
                onChange={handleChange('trims', 'labels')}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
                inputProps={{ min: 0, step: '0.01' }}
                fullWidth
              />
              <TextField
                label="Zippers/Buttons"
                type="number"
                size="small"
                value={trims.hardware ?? ''}
                onChange={handleChange('trims', 'hardware')}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
                inputProps={{ min: 0, step: '0.01' }}
                fullWidth
              />
              <TextField
                label="Other Hardware"
                type="number"
                size="small"
                value={trims.other ?? ''}
                onChange={handleChange('trims', 'other')}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
                inputProps={{ min: 0, step: '0.01' }}
                fullWidth
              />
            </Stack>
          </Box>

          <Divider />

          {/* Packaging */}
          <Box>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
              Packaging
            </Typography>
            <TextField
              label="Packaging Cost"
              type="number"
              size="small"
              value={data.packaging ?? ''}
              onChange={handleChange(null, 'packaging')}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>
              }}
              inputProps={{ min: 0, step: '0.01' }}
              sx={{ maxWidth: 300 }}
            />
          </Box>

          <Divider />

          {/* Totals */}
          <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Total Manufacturing Cost"
                type="number"
                size="small"
                value={data.totalManufacturingCost ?? ''}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  readOnly: true
                }}
                helperText="Auto-calculated from fields above"
                sx={{
                  bgcolor: 'background.paper',
                  '& .MuiInputBase-input': { fontWeight: 600 }
                }}
                fullWidth
              />
              <TextField
                label="Suggested Retail Price"
                type="number"
                size="small"
                value={data.suggestedRetail ?? ''}
                onChange={handleChange(null, 'suggestedRetail')}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
                inputProps={{ min: 0, step: '0.01' }}
                helperText="Your target retail price"
                fullWidth
              />
            </Stack>
          </Box>

          {data.totalManufacturingCost > 0 && data.suggestedRetail > 0 && (
            <Alert severity="info">
              Markup: {((data.suggestedRetail / data.totalManufacturingCost - 1) * 100).toFixed(1)}%
              {' '}| Margin: {((data.suggestedRetail - data.totalManufacturingCost) / data.suggestedRetail * 100).toFixed(1)}%
            </Alert>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default CutAndSewCostForm;
